import { DatePipe } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, AbstractControl, FormGroup } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { OwnerModel } from 'src/app/model/ownerModel.model';
import { Column } from 'src/app/modules/shared/model/column.model';
import { ExpressionResponse } from 'src/app/modules/shared/model/expressions.model';
import { AAExpressionService } from 'src/app/modules/shared/services/aaexpression-service.service';
import { PageLayoutService } from 'src/app/modules/shared/services/page-layout.service';
import { APOEntityType } from 'src/app/repo/contact-process-type.repo';
import { FlagEnum, PageLayoutMode } from 'src/app/repo/enum.repo';
import { GFilterViewIdRepo, GFilterViewPermissionLevel } from 'src/app/repo/GFilterViewIds.repo';
import { TemplateType } from 'src/app/repo/template-entity-type.repo';
import { APOSearchService } from '../../services/aposearch-service.service';
import { SearchResultsPanelComponent } from '../apo-search-results-panel/apo-search-results-panel.component';
import { TemplateAttributeModel } from 'src/app/model/templateattributemodel.model';
import { AccelaControlType, AccelaControl } from 'src/app/modules/shared/model/accelaControl.model';

@Component({
  selector: 'edit-owner-form',
  templateUrl: './edit-owner-form.component.html',
  styleUrls: ['./edit-owner-form.component.less']
})
export class EditOwnerFormComponent implements OnInit, AfterViewInit {
  @Input() model: OwnerModel;
  @Input() module: string;
  @Input() mode: PageLayoutMode;
  @Input() dataSourceType = '';
  @Input() ownerMax: number;

  @Output() saveChanges = new EventEmitter<OwnerModel[]>();
  @Output() closeDialog = new EventEmitter<boolean>();
  @Output() uiBlock = new EventEmitter<boolean>();

  @ViewChild('searchResultsPanel') searchResultsPanelComponent: SearchResultsPanelComponent;

  modelForm: FormGroup;
  controlModelMap: Map<string, string>;

  onLoadExpression: ExpressionResponse;
  onSubmitExpression: ExpressionResponse;
  onExecuteExpressions: ExpressionResponse[];
  executeFields = new Array<string>();

  originalAPOTemplatesdata: any;
  pageLayoutModes = PageLayoutMode;
  processing = false;

  searchColumns: Array<Column> = [];
  searchResults: OwnerModel[];
  editTermsText: string;
  showSearchResults: boolean;

  readonly viewId = GFilterViewIdRepo.OwnerEdit;
  readonly permissionLevel = GFilterViewPermissionLevel.APO;
  readonly permissionValue = APOEntityType.Owner;

  protected destroyActions = new Subject<boolean>();

  constructor(private formBuilder: FormBuilder, private messageService: MessageService, private datePipe: DatePipe,
    private expressionService: AAExpressionService, private pageLayoutService: PageLayoutService,
    private apoSearchService: APOSearchService) {
      this.modelForm = this.formBuilder.group({});
      this.populateControlModelMap();
      this.createFormInstance();
  }

  ngOnInit(): void {
    this.processing = true;
    this.uiBlock.emit(true);

    this.fetchExpressions();
  }

  ngAfterViewInit(): void {
  }

  /* Data Setup */
  populateControlModelMap() {
    this.controlModelMap = new Map<string, string>();
    this.controlModelMap.set('txtTitle', 'ownerTitle');
    this.controlModelMap.set('txtName', 'ownerFullName');
    this.controlModelMap.set('txtAddress1', 'mailAddress1');
    this.controlModelMap.set('txtAddress2', 'mailAddress2');
    this.controlModelMap.set('txtAddress3', 'mailAddress3');
    this.controlModelMap.set('txtCity', 'mailCity');
    this.controlModelMap.set('txtZip', 'mailZip');
    this.controlModelMap.set('ddlAppState', 'mailState');
    this.controlModelMap.set('txtFax', 'fax');
    this.controlModelMap.set('txtPhone', 'phone');
    this.controlModelMap.set('txtEmail', 'email');
    this.controlModelMap.set('ddlCountry', 'mailCountry');
  }

  createFormInstance() {
    this.processing = true;
    this.uiBlock.emit(true);
    this.modelForm = this.formBuilder.group({});
    this.pageLayoutService.getDynamicFormData(this.viewId, this.module, this.permissionLevel, this.permissionValue, 'anonymous')
      .pipe(takeUntil(this.destroyActions))
      .subscribe((resp => {
        this.processing = true;
        resp.forEach(controlData => {
          if ((this.mode === PageLayoutMode.ADMIN) || controlData.display) {
            const abstractControl = new FormControl(controlData.value);
            this.pageLayoutService.processAccelaControl(controlData, abstractControl);
            if (this.model) {
              if (controlData.templateField || this.isTemplateField(controlData.name)) {
                abstractControl.patchValue(this.getTemplateAtrributeVal(controlData.name));
              } else {
                abstractControl.patchValue(this.model[this.controlModelMap.get(controlData.name)]);
              }
            } else {
              abstractControl.patchValue(null);
            }

            if (controlData.display && (controlData.type !== AccelaControlType.Separator)) {
              this.modelForm.addControl(controlData.name, abstractControl);
            }

          }
        });
        this.handleOnLoadExpression();

        if (this.mode !== PageLayoutMode.ADMIN) {
          this.processing = true;
          this.uiBlock.emit(true);

          this.expressionService
            .getExpressionJsScriptForExecute(this.viewId, this.module, this.permissionLevel, this.permissionValue, 'anonymous')
            .pipe(takeUntil(this.destroyActions))
            .subscribe(res => {
              if (res && res.length > 0) {
                this.onExecuteExpressions = res;
                res.forEach(data => {
                  Object.keys(data.inputFields).forEach(fieldSet => {
                    data.inputFields[fieldSet].forEach(field => {
                      const inputField = this.modelForm.get(field);
                      if(inputField && !(this.executeFields.findIndex(str => str === field) >= 0)){
                        this.executeFields.push(field);
                      }
                    });
                  });
                });
              }
              this.processing = false;
              this.uiBlock.emit(false);
            }, err => {
              this.processing = false;
              this.uiBlock.emit(false);
              throw err;
            });
        }

      })
        , error => {
          this.messageService.clear();
          this.messageService.add({
            severity: 'error',
            summary: error,
            closable: false
          });
          this.processing = false;
          this.uiBlock.emit(false);
        });
    this.getOriginalAPOTemplatesdata();
  }

  updateFormInstance(owner: OwnerModel){
    this.model = owner;

    Object.keys(this.modelForm.controls).forEach(key => {
      if (this.isTemplateField(key)) {
        this.modelForm.controls[key].patchValue(this.getTemplateAtrributeVal(key));
      } else {
        this.modelForm.controls[key].patchValue(this.model[this.controlModelMap.get(key)]);
      }
    });
    this.handleOnLoadExpression();

  }

  /* Expression Handling */
  fetchExpressions() {
    //Expression loading
    this.expressionService
      .getExpressionJsScriptOnLoad(this.viewId, this.module, this.permissionLevel, this.permissionValue, 'anonymous')
      .pipe(takeUntil(this.destroyActions))
      .subscribe(res => {
        this.onLoadExpression = res;
        this.processing = false;
        this.uiBlock.emit(false);
      }, err => {
        this.processing = false;
        this.uiBlock.emit(false);
        throw err;
      });

    this.expressionService
      .getExpressionJsScriptOnSubmit(this.viewId, this.module, this.permissionLevel, this.permissionValue, 'anonymous')
      .pipe(takeUntil(this.destroyActions))
      .subscribe(res => {
        this.onSubmitExpression = res;
      });
  }

  handleOnLoadExpression(){
    if (this.onLoadExpression) {
      this.expressionService
        .invokeExpression(this.onLoadExpression, this.modelForm)
        .pipe(takeUntil(this.destroyActions))
        .subscribe(res => {
          this.processing = false;
          this.uiBlock.emit(false);
        }, err => {
          this.processing = false;
          this.uiBlock.emit(false);
          throw err;
        });
    } else {
      this.processing = false;
      this.uiBlock.emit(false);
    }
  }

  invokeExpressionForExecute(event: AbstractControl) {
    if (this.executeFields.findIndex(str => str === event.controlData.name) >= 0) {
      this.onExecuteExpressions.forEach(data => {
        this.executeFields.forEach(field =>{
          const inputField = this.modelForm.get(field);
          if (inputField) {
            const ctrlData = inputField.controlData;
            ctrlData.value = inputField.value;
            data.inputFieldProperties[ctrlData.name] = inputField.controlData;
          }
        });
        this.expressionService.invokeExpression(data, this.modelForm, null).subscribe();

      });
    }
  }

  /* Template Handling */
  getOriginalAPOTemplatesdata() {
    this.pageLayoutService.getAPOTemplateData(TemplateType.CAP_ADDRESS).then((res) => {
      if(res){
        this.originalAPOTemplatesdata = JSON.parse(res.toString());
      }
    });
  }

  getTemplateAtrributeVal(fieldname: string): string | Date {
    let fieldval: string | Date;
    if (this.model?.templates != null) {
      this.model.templates.forEach((templateData) => {
        if (templateData.attributeName === fieldname) {
          if (templateData.attributeValueDataType === 'Date') {
            fieldval = new Date(templateData.attributeValue);
          } else {
            fieldval = templateData.attributeValue;
          }
          return fieldval;
        }
      });
    }
    return fieldval;
  }

  isTemplateField(templateName: string): boolean {
    let isTemplate = false;
    if (this.model?.templates != null) {
      this.model.templates.forEach((templateData) => {
        if (templateData.attributeName === templateName) {
          isTemplate = true;
          return isTemplate;
        }
      });
    }
    return isTemplate;
  }

  /* Cross Page Messaging */
  resize(event) {
    window.parent.postMessage('CommunityView:ResizeDialog', '*');
  }

  collapseSize(event){
    window.parent.postMessage('CommunityView:RemoveMin', '*');
  }

  /* Perform Search */
  performSearch() {
    //this.displaySearchResults = true;
    if (this.processing || this.isSearchFormEmpty()) {
      return;
    }
    this.processing = true;
    this.uiBlock.emit(true);
    const valid = this.validateForm();
    if (!valid) {
      this.messageService.clear();
      this.messageService.add({
        severity: 'error',
        summary: $localize`Not all fields are filled out correctly. Please check again.`,
        closable: false
      });
      this.processing = false;
      return;
    }
    const ownerModel = new OwnerModel();
    this.mapUiFieldsToModel(ownerModel);
    this.editTermsText = this.getEditTermText();
    const searchFormOwnerModel = ownerModel;
    this.apoSearchService.getLookupResultsFromOwnerModel(ownerModel).subscribe(res => {
      this.searchResults = res;
      //Clearing selected search results before showing the lookup search results dialog
      this.searchResultsPanelComponent.selectedData = [];
      this.showSearchResults = true;
      this.uiBlock.emit(false);
      this.processing = false;
    });
  }

  backToSearch(){
    this.showSearchResults = false;
  }

  validateForm(): boolean {
    let valid = true;
    for (const simpleElemName in this.modelForm.controls) {
      if (Object.prototype.hasOwnProperty.call(this.modelForm.controls, simpleElemName)) {
        const formControl = this.modelForm.controls[simpleElemName] as FormControl;
        if (formControl.validator) {
          formControl.markAsDirty();
        }
        if (formControl.errors && Object.keys(formControl.errors).length > 0) {
          valid = false;
        }
      }
    }
    return valid;
  }

  isSearchFormEmpty(): boolean {
    const isEmpty = Object.values(this.modelForm.value).every(x => x === null || x === '');
    return isEmpty;
  }

  mapUiFieldsToModel(ownerModel: OwnerModel): void {
    this.mapUItoModel(ownerModel, 'ownerTitle', 'txtTitle');
    this.mapUItoModel(ownerModel, 'ownerFullName', 'txtName');
    this.mapUItoModel(ownerModel, 'mailAddress1', 'txtAddress1');
    this.mapUItoModel(ownerModel, 'mailAddress2', 'txtAddress2');
    this.mapUItoModel(ownerModel, 'mailAddress3', 'txtAddress3');
    this.mapUItoModel(ownerModel, 'mailCity', 'txtCity');
    this.mapUItoModel(ownerModel, 'mailZip', 'txtZip');
    this.mapUItoModel(ownerModel, 'mailState', 'ddlAppState');
    this.mapUItoModel(ownerModel, 'fax', 'txtFax');
    this.mapUItoModel(ownerModel, 'phone', 'txtPhone');
    this.mapUItoModel(ownerModel, 'email', 'txtEmail');
    this.mapUItoModel(ownerModel, 'mailCountry', 'ddlCountry');
    ownerModel.ownerStatus = 'A';
    ownerModel.auditStatus = 'A';

  }

  mapUItoModel(ownerModel: OwnerModel, property: string, controlName: string) {
    if (this.modelForm.controls[controlName]) {
      ownerModel[property] = this.modelForm.controls[controlName].value;
    }
  }
  mapUItoTemplateAttributeModel(templatesData: TemplateAttributeModel[]): TemplateAttributeModel[] {
    if (templatesData == null) {
      return null;
    }
    templatesData.forEach((templateData, index) => {
      if (this.modelForm.controls[templateData.attributeName] && templateData.vchFlag === FlagEnum.Y) {
        if (this.modelForm.controls[templateData.attributeName].controlData.type === AccelaControlType.Date) {
          const dateVal = this.modelForm.controls[templateData.attributeName].value;
          templatesData[index]['attributeValue'] = (dateVal === null) ? null : this.datePipe.transform(dateVal, 'MM/dd/yy');
        } else {
          templatesData[index]['attributeValue'] = this.modelForm.controls[templateData.attributeName].value;
        }
      }
    });
    return templatesData;
  }
  getEditTermText() {
    let editTermsText = '';
    let addressControlData: AccelaControl;
    let name: string;
    let value: string;
    for (const key in this.modelForm.value) {
      if (this.modelForm.value[key] != null) {
        addressControlData = this.modelForm.controls[key].controlData;
        name = addressControlData.label;
        value = this.modelForm.value[key];
        if (addressControlData.type === AccelaControlType.Dropdown) {
          const dIndex = addressControlData.options.findIndex(x => x.value === value);
          if (dIndex > -1) {
            value = addressControlData.options[dIndex].label;
          }
        }
        if (addressControlData.type === AccelaControlType.Date) {
          value = this.datePipe.transform(value, 'MM/dd/yy');
        }
        if (value !== '') {
          editTermsText += `<strong>${name.replace(':', '')}</strong> contains: <strong>${value}</strong> | `;
        }
      }
    }
    return editTermsText;
  }

  addResultsFromSearch(event: Event){
    this.showSearchResults = false;
    this.saveChanges.emit(this.searchResultsPanelComponent.selectedData);
  }

  /* UI Operations */
  close(){
    this.closeDialog.emit(false);
  }

  cancelSearch(event: Event){
    this.showSearchResults = false;
  }

  /* Save Changes */
  saveChangesToModel(){
    this.saveChanges.emit([this.model]);
  }

}
